import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from '../api/axios';
import CountrySelect from './CountrySelect';
import CurrencySelect from './CurrencySelect';
import BankAccountForm from './BankAccountForm';
import ConfirmModal from './ConfirmModal';
import ApiErrorModal from './ApiErrorModal';
import { TrashIcon, DocumentIcon, UploadIcon, CheckIcon, PencilIcon } from './Icons';
import { detectUserLocationData } from '../utils/geo';

export default function BankAccountsManager({ userId, shouldFetch = true, onLoaded }) {
  const { accountIndex } = useParams();
  
  const [banks, setBanks] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total: 0, per_page: 5 });
  const [isAdding, setIsAdding] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: "", message: "", details: null });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [isReloading, setIsReloading] = useState(false);
  const [newBank, setNewBank] = useState({
    country_code: '',
    bank_name: '',
    branch_name: '',
    branch_code: '',
    account_holder_name: '',
    account_number: '',
    iban: '',
    swift_bic: '',
    routing_code: '',
    currency: 'USD',
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  
  const [error, setError] = useState(null);

  const [currencies, setCurrencies] = useState([]);

  const fetchBanks = async (page = 1) => {
    setIsReloading(true);
    try {
      const res = await axios.get(`/users/me/bank-accounts?page=${page}&per_page=5`);
      const fetchedBanks = res.data.bank_accounts || [];
      setBanks(fetchedBanks);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      }
      if (onLoaded) onLoaded();
    } catch (e) {
      console.error('Failed to load bank accounts', e);
    } finally {
      setIsReloading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const res = await axios.get('/auth/currencies');
      if (res.data?.data) {
        setCurrencies(res.data.data);
      }
    } catch (e) {
      console.error('Failed to load currencies', e);
    }
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchBanks(1);
    }
  }, [shouldFetch, userId]);

  useEffect(() => {
    if ((isAdding || editingRowId !== null) && currencies.length === 0) {
      fetchCurrencies();
    }
  }, [isAdding, editingRowId, currencies.length]);





  const handleCountryChange = (c) => {
    setNewBank({ ...newBank, country_code: c.iso2, routing_code: '', iban: '' });
  };

  const getRoutingLabel = (country) => {
    if (country === 'US') return 'ABA Routing Number';
    if (country === 'GB') return 'Sort Code';
    if (country === 'AU') return 'BSB Code';
    if (country === 'IN') return 'IFSC Code';
    return 'Routing Code';
  };

  const isIbanCountry = (c) => ['GB', 'FR', 'DE', 'IT', 'ES', 'NL', 'AE', 'SA', 'QA', 'IE', 'CH', 'AT', 'BE', 'PT'].includes(c);
  const isRoutingCountry = (c) => ['US', 'GB', 'AU', 'IN'].includes(c);

  const validateBank = () => {
    if (!newBank.country_code) return "Country is required";
    if (!newBank.bank_name) return "Bank name is required";
    if (!newBank.account_holder_name) return "Account holder name is required";
    if (!newBank.currency) return "Currency is required";

    const country = newBank.country_code;

    if (isIbanCountry(country) && !newBank.iban) {
      return "IBAN is required for this country";
    }

    if (isRoutingCountry(country)) {
      if (country === 'US' && !/^\d{9}$/.test(newBank.routing_code)) return "US Routing must be 9 digits";
      if (country === 'GB' && !/^(\d{6}|\d{2}-\d{2}-\d{2})$/.test(newBank.routing_code)) return "UK Sort Code must be 6 digits";
      if (country === 'AU' && !/^\d{6}$/.test(newBank.routing_code)) return "BSB must be 6 digits";
      if (country === 'IN' && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(newBank.routing_code)) return "Invalid IFSC format";
    }

    if (!isIbanCountry(country) && !newBank.account_number) {
      return "Account Number is required";
    }

    if (newBank.swift_bic && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(newBank.swift_bic)) {
      return "Invalid SWIFT/BIC code format";
    }

    if (!documentFile) {
      return "Please upload a bank statement or passbook image";
    }

    return null;
  };

  
  const handleUpdate = async (formData, file) => {
    setSaving(true);
    try {
      const form = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && key !== 'has_document') {
          let val = formData[key];
          if (typeof val === 'boolean') val = val ? '1' : '0';
          form.append(key, val);
        }
      });
      if (file) {
        form.append('document', file);
      }
      form.append('_method', 'PUT'); // Laravel spoofing

      const res = await axios.post(`/users/me/bank-accounts/${formData.id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBanks(banks.map(b => b.id === formData.id ? res.data.bank_account : b));
      setEditingRowId(null);
      setEditingData(null);
    } catch (e) {
      console.error('Failed to update bank account', e);
      setErrorModal({
        isOpen: true,
        title: 'Update Failed',
        message: 'Failed to update bank account.',
        details: e.response?.data?.message || e.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const err = validateBank();
    if (err) {
      setError(err);
      return;
    }
    
    setSaving(true);
    setError(null);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      Object.keys(newBank).forEach(key => {
        if (newBank[key]) formData.append(key, newBank[key]);
      });
      formData.append('document', documentFile);

      await axios.post('/users/me/bank-accounts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setNewBank({
        country_code: '',
        bank_name: '',
        branch_name: '',
        branch_code: '',
        account_holder_name: '',
        account_number: '',
        iban: '',
        swift_bic: '',
        routing_code: '',
        currency: 'USD',
      });
      setDocumentFile(null);
      setIsAdding(false);
      await fetchBanks(1);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to add bank account");
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const requestDelete = (id) => setDeleteConfirmId(id);
  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/users/me/bank-accounts/${deleteConfirmId}`);
      await fetchBanks(pagination.current_page);
      setDeleteConfirmId(null);
    } catch (e) {
      setDeleteConfirmId(null);
      setErrorModal({
        isOpen: true,
        title: 'Delete Failed',
        message: 'Failed to delete bank account.',
        details: e.response?.data?.message || e.message
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  

  

  const handleReload = async () => {
    await fetchBanks(pagination.current_page);
  };

  const renderBadge = (status) => {
    if (status === 'verified') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">✓ Verified</span>;
    if (status === 'rejected') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border border-red-300 dark:border-red-700">Rejected</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">Pending Review</span>;
  };

  const handleAddBankClick = async () => {
    setIsAdding(true);
    const loc = await detectUserLocationData();
    setNewBank(prev => ({ ...prev, country_code: loc.iso2, currency: loc.currency }));
  };

  
  const handleViewDocument = async (id) => {
    try {
      const res = await axios.get(`/users/me/bank-accounts/${id}/document`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      console.error('Failed to get document URL', e);
      alert('Failed to get document URL. It may have expired or rate limit exceeded.');
    }
  };

  return (
    <>

      <ConfirmModal
        isOpen={!!deleteConfirmId}
        title="Delete Bank Account"
        message="Are you sure you want to permanently delete this bank account? This action cannot be undone."
        confirmText="Delete Account"
        isDangerous={true}
        isLoading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmId(null)}
      />
      <ApiErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onCancel={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
        onReload={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
      />
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Bank Accounts</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Select the radio button to set a bank account as primary.</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            type="button" 
            onClick={handleReload} 
            disabled={isReloading || isAdding || editingRowId !== null}
            className={`p-1 text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-50 ${(isAdding || editingRowId !== null) ? 'hidden' : ''}`}
            title="Reload Bank Accounts"
          >
            <svg className={`w-4 h-4 ${isReloading ? 'animate-spin text-indigo-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button 
            type="button" 
            disabled={pagination.total >= 20 || isAdding || editingRowId !== null || saving}
            onClick={handleAddBankClick} 
            title={pagination.total >= 20 ? 'Maximum limit of 20 bank accounts reached' : ''}
            className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Bank Account
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {banks.map((b) => {
          const isPrimary = b.is_primary;
          const isBusy = saving || isAdding || editingRowId !== null;
          
          if (editingRowId === b.id) {
              return (
                <BankAccountForm 
                  key={b.id}
                  initialData={b}
                  currencies={currencies}
                  onSave={handleUpdate}
                  onCancel={() => setEditingRowId(null)}
                  saving={saving}
                />
              );
            }
            return (
              <div key={b.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700">

              <div className="flex items-start gap-3">
                <label className={`cursor-pointer flex items-center pt-0.5 ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                  <input 
                    type="radio" 
                    name="primaryBankRadio" 
                    checked={isPrimary} 
                    onChange={async () => {
                      if (saving) return;
                      setSaving(true);
                      try {
                        await axios.put(`/users/me/bank-accounts/${b.id}/primary`);
                        await fetchBanks(pagination.current_page);
                      } catch (err) {
                        console.error(err);
                        alert('Failed to update primary bank account.');
                      } finally {
                        setSaving(false);
                      }
                    }} 
                    disabled={isBusy}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-50" 
                  />
                </label>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/u/${accountIndex || 0}/dashboard/profile/bank/${b.id}`} className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors hover:underline" title="View full details">{b.bank_name || 'Bank Account'}</Link>
                    {isPrimary && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">Holder: {b.account_holder_name}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Account: <span className="font-mono">{b.account_number || b.iban}</span> ({b.currency})</p>
                  {b.routing_code && <p className="text-xs text-slate-600 dark:text-slate-400">Routing/Sort/BSB: <span className="font-mono">{b.routing_code}</span></p>}
                  {b.swift_bic && <p className="text-xs text-slate-600 dark:text-slate-400">SWIFT: <span className="font-mono">{b.swift_bic}</span></p>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2.5 mt-3 sm:mt-0">
                {renderBadge(b.status)}
                <div className="flex items-center gap-2">
                  {b.has_document && (
                    <button type="button" onClick={() => handleViewDocument(b.id)} className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1">
                      <DocumentIcon className="w-3.5 h-3.5" /> View Doc
                    </button>
                  )}
                  
                  <button 
                      type="button" 
                      onClick={() => {
                        setEditingRowId(b.id);
                        setEditingData(b);
                      }} 
                      disabled={isBusy}
                      className="p-1 text-slate-400 hover:text-indigo-500 transition-colors disabled:opacity-50"
                      title="Edit Bank Account"
                    >
                      <PencilIcon className="w-4 h-4" />
                  </button>
                  <button 
                    type="button" 
                    onClick={() => requestDelete(b.id)} 
                    disabled={isBusy}
                    className="p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                    title="Delete Bank Account"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {banks.length === 0 && !isAdding && (
          <p className="text-sm text-slate-500 italic p-4 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">No bank accounts added yet.</p>
        )}

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between pt-2 px-1 text-xs text-slate-500 dark:text-slate-400">
            <span>Showing {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagination.current_page <= 1}
                onClick={() => fetchBanks(pagination.current_page - 1)}
                className="px-2.5 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition text-xs font-medium"
              >
                Previous
              </button>
              <span>Page {pagination.current_page} of {pagination.total_pages}</span>
              <button
                type="button"
                disabled={pagination.current_page >= pagination.total_pages}
                onClick={() => fetchBanks(pagination.current_page + 1)}
                className="px-2.5 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition text-xs font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {isAdding && (
        <div className="mt-4 p-5 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 rounded-xl">
          <h4 className="text-sm font-semibold mb-3 text-slate-900 dark:text-white">Add New Bank Account</h4>
          {error && <div className="mb-3 p-2.5 bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs rounded-lg border border-red-200 dark:border-red-800">{error}</div>}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Country</label>
              <CountrySelect value={newBank.country_code} onChange={handleCountryChange} preload={true} />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Bank Name</label>
              <input type="text" value={newBank.bank_name} onChange={e => setNewBank({...newBank, bank_name: e.target.value})} placeholder="e.g. Chase, HSBC" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Account Holder Name</label>
              <input type="text" value={newBank.account_holder_name} onChange={e => setNewBank({...newBank, account_holder_name: e.target.value})} placeholder="Name as appears on account" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Currency</label>
              <CurrencySelect
                value={newBank.currency}
                currencies={currencies}
                onChange={c => setNewBank({...newBank, currency: c.code})}
              />
            </div>

            {isIbanCountry(newBank.country_code) ? (
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">IBAN</label>
                <input type="text" value={newBank.iban} onChange={e => setNewBank({...newBank, iban: e.target.value.toUpperCase()})} placeholder="GB00..." className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Account Number</label>
                <input type="text" value={newBank.account_number} onChange={e => setNewBank({...newBank, account_number: e.target.value})} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
              </div>
            )}

            {isRoutingCountry(newBank.country_code) && (
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">{getRoutingLabel(newBank.country_code)}</label>
                <input type="text" value={newBank.routing_code} onChange={e => setNewBank({...newBank, routing_code: e.target.value})} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">SWIFT / BIC (Optional)</label>
              <input type="text" value={newBank.swift_bic} onChange={e => setNewBank({...newBank, swift_bic: e.target.value.toUpperCase()})} placeholder="8 or 11 characters" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Branch Name (Optional)</label>
              <input type="text" value={newBank.branch_name} onChange={e => setNewBank({...newBank, branch_name: e.target.value})} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Verification Document (Passbook Front Page or Bank Statement)</label>
              <div className="flex items-center justify-center w-full">
                <label 
                  className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-indigo-500"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      setDocumentFile(e.dataTransfer.files[0]);
                    }
                  }}
                >
                  <div className="flex flex-col items-center justify-center pt-3 pb-3 text-slate-500">
                    {documentFile ? (
                      <>
                        <CheckIcon className="w-7 h-7 text-emerald-500 mb-1" />
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{documentFile.name}</p>
                        <p className="text-xs text-slate-400">{(documentFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </>
                    ) : (
                      <>
                        <UploadIcon className="w-7 h-7 mb-1.5 text-slate-400" />
                        <p className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300">Click to upload or drag and drop</p>
                        <p className="text-[11px] text-slate-400">PDF, PNG, JPG (Max 5MB - Original Resolution)</p>
                      </>
                    )}
                  </div>
                  <input type="file" className="hidden" accept=".pdf,image/png,image/jpeg,image/jpg" onChange={(e) => setDocumentFile(e.target.files[0])} />
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2.5">
            <button type="button" onClick={() => setIsAdding(false)} className="px-3.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-3.5 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition shadow-sm">
              {saving ? `Saving (${uploadProgress}%)` : 'Save Bank Account'}
            </button>
          </div>
        </div>
      )}

          </div>
    </>
  );
}
